import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { CHECK_IN_FIELDS, checkInGroups, inVocabularyOrder } from "../lib/checkInFields";
import { saveLabel, targetDays, targetSentence } from "../lib/taskDay";
import { tellTheClient } from "../lib/assignPush";

/* A DAILY TASK, ON THE WEB (W4, 2026-09-18).
 *
 * 🔴 **A TASK IS A `coach_workouts` ROW WITH `kind = 'task'`, ASSIGNED THROUGH THE SAME RPC AS A
 * WORKOUT — the phone's own decision, kept.** `CoachTaskEditor.save()` says why: *"copy-on-assign,
 * the one-source-per-day guard and every RLS policy apply to it unchanged, and nothing here is a
 * second definition of how a coach's work reaches a client."* This screen writes the same shape.
 *
 * ⚠️ **ASSIGNED FROM THE TEMPLATE, WHICH IS CORRECT HERE AND ONLY HERE.** The standing rule is
 * that a week is assigned from LAST WEEK'S COPY so progression is not reset — but this row was
 * written a second ago and has no previous week. Every later week copies forward as usual.
 *
 * ⚠️ **THE PROMPT AND THE FIELDS TRAVEL TOGETHER OR NOT AT ALL** — the phone's rule, and its
 * reasoning: a prompt with no field list is a check-in whose contents the athlete's sheet has to
 * guess; a field list with no prompt is a note quietly carrying measurements nothing asks for.
 */

type Props = {
  clientId: string;
  coachId: string;
  /**
   * 🔴 W30(a) — THE DAY THE COACH REACHED FOR, AS A DAY KEY, WITH NO DEFAULT.
   *
   * It was a `Date` and its only caller passed `today`, hard-coded, while the calendar beside it
   * paged freely — so a task reached for on `Mon 19 Oct` was written into this week and the form
   * said nothing (`docs/EVERFIT-MAP.md` §9-ter). A `YYYY-MM-DD` rather than a `Date` because
   * that is what the `+` has, what the RPC takes and what the sentence prints: a `Date` in
   * between is one more place a midnight can be read in the wrong zone.
   */
  dayKey: string;
  onSaved: () => void;
  onClose: () => void;
};

export function TaskEditor({ clientId, coachId, dayKey, onSaved, onClose }: Props) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [isCheckIn, setIsCheckIn] = useState(false);
  /* 🔴 OFFERED, NEVER OWED — his 2026-10-07 word (Sunday's menu, the non-Monday weigh-ins).
     Orthogonal to the check-in: an optional weigh-in is both. The phone's editor has the same
     switch (`CoachTaskEditor.isOptional`); both write `coach_workouts.task_optional`. */
  const [isOptional, setIsOptional] = useState(false);
  const [fields, setFields] = useState<string[]>(["weight"]);
  const [everyDay, setEveryDay] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (el !== null && !el.open) el.showModal();
    heading.current?.focus();
    return () => { if (el !== null && el.open) el.close(); };
  }, []);

  const canSave = title.trim() !== "" && (!isCheckIn || fields.length > 0);

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);

    // 1 — the task row. ⚠️ `kind: "task"` is what the CHECK admits (`workout` | `task`), and
    // `task_prompt` may only be `bodyweight` and only on a task — both measured from
    // `pg_constraint`, so a wrong value is refused rather than stored.
    const { data: made, error: mkErr } = await supabase
      .from("coach_workouts")
      .insert({
        coach_id: coachId,
        name: title.trim(),
        description: note.trim() === "" ? null : note.trim(),
        kind: "task",
        task_prompt: isCheckIn ? "bodyweight" : null,
        // Stored in the VOCABULARY's order, never the order the coach happened to tap.
        check_in_fields: isCheckIn ? inVocabularyOrder(fields) : null,
        // `task_optional` is admitted only on a task (the CHECK) — which this row is.
        task_optional: isOptional,
      })
      .select("id")
      .single();
    if (mkErr !== null) { setSaving(false); setError(mkErr.message); return; }

    // 2 — assign it, through the SAME RPC the phone and the workout panel use. Sequential, for
    // the reason `AssignPanel` records: two concurrent writes of one source race the unique index
    // and one reports a duplicate the coach did not create.
    /* 🔴 THE SAME FUNCTION THE SENTENCE ABOVE THE BUTTON PRINTS FROM. A form that says seven
       days out of its own arithmetic while the loop writes six is this repo's defining bug class
       with a calendar in it; `targetDays` is asked once, by both. */
    const days = targetDays(dayKey, everyDay);
    const failures: string[] = [];
    let firstId: string | null = null, written = 0;
    for (const d of days) {
      const { data: newId, error: asErr } = await supabase.rpc("assign_coach_workout", {
        p_workout_id: made.id,
        p_client_id: clientId,
        p_scheduled_date: d,
        p_note: "",
      });
      // ⚠️ A DAY THAT ALREADY HOLDS THIS SOURCE IS NOT A FAILURE — the phone's own words, and the
      // unique index is what makes re-saving a no-op rather than an error. Anything else is.
      if (asErr !== null && !/one_source_per_day|duplicate key/i.test(asErr.message)) {
        failures.push(`${d}: ${asErr.message}`);
      }
      if (asErr === null) { written += 1; firstId ??= newId ?? null; }
    }
    tellTheClient(firstId, written);   // QUEUE N2c fix 2: one alert for the act, only for days written
    setSaving(false);
    if (failures.length > 0) {
      // ⚠️ The task EXISTS at this point, and saying so is the difference between a coach who
      // retries and one who creates a second copy of the same task.
      setError(`The task was created, but some days could not be set: ${failures.join("; ")}`);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <dialog ref={dialogRef} className="ht-window task-window"
            aria-labelledby="task-window-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame">
        <header className="ht-window-head">
          <div className="ht-window-titles">
            <p className="page-eyebrow">Client</p>
            <h2 className="ht-title" id="task-window-title" tabIndex={-1} ref={heading}>
              New task
            </h2>
          </div>
          <button type="button" className="ht-button ht-button-quiet ht-window-close"
                  onClick={onClose}>Close</button>
        </header>

        <div className="ht-window-body task-form">
          <label className="exercise-field">
            <span className="ht-caption">What to do</span>
            <input className="ht-input" value={title} placeholder="Wrist series"
                   onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label className="exercise-field">
            <span className="ht-caption">Note <span className="exercise-hint">optional</span></span>
            <textarea className="ht-input" rows={2} value={note}
                      onChange={(e) => setNote(e.target.value)} />
          </label>

          <label className="autoapply-row">
            <input type="checkbox" checked={everyDay}
                   onChange={(e) => setEveryDay(e.target.checked)} />
            <span className="ht-callout">Every day this week</span>
          </label>

          {/* 🔴 W30(a) — THE FORM SAYS WHICH DAYS IT WILL WRITE TO.
              Theirs prints `SET DATE · Sep 16, 2026 (Wed)` and lets the coach change it
              (§9-ter); ours cannot change it here — the day is chosen by the `+` on the cell —
              so what it owes is the STATEMENT. ⚠️ `aria-live="polite"` on this paragraph and
              nowhere else in this form: the sentence changes under the checkbox above it, and a
              coach using a screen reader would otherwise tick a box and hear nothing. */}
          <p className="ht-caption exercise-hint task-target" aria-live="polite">
            {targetSentence(dayKey, everyDay)}
          </p>

          <label className="autoapply-row">
            <input type="checkbox" checked={isCheckIn}
                   onChange={(e) => setIsCheckIn(e.target.checked)} />
            <span className="ht-callout">Ask for measurements (a check-in)</span>
          </label>

          <label className="autoapply-row">
            <input type="checkbox" checked={isOptional}
                   onChange={(e) => setIsOptional(e.target.checked)} />
            <span className="ht-callout">Optional</span>
          </label>
          {/* The consequence, said where the choice is made — the phone's footer, word for word. */}
          <p className="ht-caption exercise-hint task-optional-hint" aria-live="polite">
            {isOptional
              ? "Offered, never owed: no badge, never skipped, outside their counts. They can do it any time that day."
              : "Owed on its day: a badge until it is done, skipped when its time passes."}
          </p>

          {isCheckIn && (
            <fieldset className="exercise-fieldset task-checkins">
              <legend className="ht-caption">
                What to ask for
                {/* ⚠️ Said out loud rather than left to be discovered: these are the CHOICES, and
                    the things that are always there are not choices. The enum's own rule. */}
                <span className="exercise-hint"> — the date, notes and photos are always asked</span>
              </legend>
              {checkInGroups().map((g) => (
                <div key={g.group} className="task-checkin-group">
                  <p className="ht-caption exercise-hint">{g.title}</p>
                  <div className="exercise-checks">
                    {g.fields.map((f) => (
                      <label key={f.key} className="exercise-check">
                        <input type="checkbox" checked={fields.includes(f.key)}
                               onChange={() => setFields(
                                 fields.includes(f.key)
                                   ? fields.filter((x) => x !== f.key)
                                   : [...fields, f.key])} />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {fields.length === 0 && (
                <p className="ht-callout exercise-error">
                  Choose at least one measurement, or turn the check-in off.
                </p>
              )}
            </fieldset>
          )}
        </div>

        <footer className="ht-window-foot">
          {error !== null && <p className="ht-callout exercise-error" role="alert">{error}</p>}
          <button type="button" className="ht-button ht-button-primary"
                  disabled={!canSave || saving}
                  onClick={() => { void save(); }}>
            {saving ? "Saving…" : saveLabel(dayKey, everyDay)}
          </button>
        </footer>
      </div>
    </dialog>
  );
}

/** How many fields the vocabulary has — used by the tests to pin that none were dropped. */
export const TASK_FIELD_COUNT = CHECK_IN_FIELDS.length;
